/* eslint-disable max-lines */

import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { type Observable, take } from 'rxjs';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  switchMap,
  shareReplay,
  withLatestFrom,
  takeUntil,
} from 'rxjs';
import { filter } from 'rxjs/operators';

import { debounceAsap } from '@core/common-legacy';
import { injectUntilDestroyed, stableStringify } from '@core/utils-legacy';
import {
  getRecordsId,
  getStoreGroupId,
  getStoreSwimlaneId,
  ViewGroupsSelectors,
  ViewRecordsActions,
  ViewSwimlaneActions,
} from '@core/views-data';
import type {
  AnyGroup,
  Swimlane,
  SwimlanesData,
  GroupingState,
  SwimlaneGroupingState,
  GroupsState,
  ViewRecord,
  MoveToParams,
} from '@core/views-types';
import {
  injectViewDataScope,
  LayoutBoardPublicService,
  dataSourceService$,
  isAllPagesLoaded,
} from '@core/views-util';

import { ViewDataService } from '../../../view-data.service';
import { LayoutSharedService } from '../layout-shared.service';

@Injectable()
export class LayoutBoardDataService implements LayoutBoardPublicService {
  private readonly viewDataScope = injectViewDataScope();
  private readonly viewDataService = inject(ViewDataService);
  private readonly layoutSharedService = inject(LayoutSharedService);
  private readonly actions$ = inject(Actions);

  public readonly GROUP_PAGE_SIZE = 10;
  public readonly SWIMLANE_PAGE_SIZE = 10;
  public readonly RECORDS_PAGE_SIZE = 15;

  public groupsState?: GroupingState;
  public swimlanesData$: Observable<SwimlanesData> = combineLatest({
    swimlaneState: this.viewDataService.swimlaneState$,
    groupState: this.viewDataService.groupState$,
    hiddenGroups: this.layoutSharedService.hiddenGroups$,
  }).pipe(
    debounceAsap(),
    filter(({ groupState }) => !!groupState[1]),
    switchMap(({ swimlaneState, groupState, hiddenGroups }) =>
      this.selectRecords(swimlaneState, groupState, hiddenGroups),
    ),
    injectUntilDestroyed(),
    shareReplay(1),
  );

  private untilDestroyed = injectUntilDestroyed();
  private store = inject(Store);

  constructor() {
    combineLatest([this.viewDataService.groupState$, this.layoutSharedService.hiddenGroups$])
      .pipe(debounceAsap(), this.untilDestroyed)
      .subscribe(([groupState, hiddenGroups]) => {
        this.groupsState = groupState;
        const [_, groupData, groupMeta] = groupState;
        if (!groupData || !groupMeta) return;

        this.loadGroupsIfAllHidden(groupData, groupMeta, hiddenGroups);
      });
    this.watchParamsChanges();
    this.handleGroupChanges();

    const { formParams$ } = this.viewDataService;
    formParams$.pipe(this.untilDestroyed).subscribe(params => {
      const { group, layout } = params;
      if (group?.configId && layout?.swimlane?.configId) {
        this.loadSwimlanes(params, 1);
      }
    });

    this.watchDataRefresh();
  }

  public loadGroupsIfAllHidden(
    groupData: GroupsState,
    groupMeta: { isPending: boolean },
    hiddenGroups: Record<string, boolean>,
  ) {
    const { groups, meta } = groupData;
    const { isPending } = groupMeta;
    const isAllLoaded = meta ? isAllPagesLoaded(meta) : false;
    const isAllLoadedHidden = groups
      ? groups.every(group => hiddenGroups[stableStringify(group.id)])
      : false;
    if (!isAllLoaded && isAllLoadedHidden && !isPending) {
      this.loadGroups();
    }
  }

  public loadGroups() {
    const { groupingIdControl } = this.viewDataService;
    if (!groupingIdControl.value) return;

    const [groupingId, groupsState] = this.groupsState ?? [null, null, null];
    const canLoadMore = !groupsState || !isAllPagesLoaded(groupsState.meta);
    const { layout } = this.viewDataService.getFormParams();
    const swimlaneGroupingId = layout?.swimlane?.configId ?? null;
    const groupStoreId = getStoreGroupId(
      this.viewDataService.dataSourceId,
      groupingId!,
      swimlaneGroupingId,
    );
    const groupsMeta = this.store.selectSignal(ViewGroupsSelectors.meta(groupStoreId))();
    if (groupsMeta?.isPending || groupsMeta?.isFailed || !canLoadMore) return;

    if (groupingId !== groupingIdControl.value) {
      this.viewDataService.loadGroups(1, this.GROUP_PAGE_SIZE);
    } else if (groupingId === groupingIdControl.value) {
      this.viewDataService.loadGroups((groupsState?.meta.page ?? 0) + 1, this.GROUP_PAGE_SIZE);
    }
  }

  public loadSwimlanes(params: ReturnType<ViewDataService['getFormParams']>, page: number) {
    const { filters, operator, group, layout } = params;
    this.store.dispatch(
      ViewSwimlaneActions.fetch({
        payload: {
          storeSwimlaneGroupId: getStoreSwimlaneId(
            this.viewDataService.dataSourceId,
            group!.configId,
            layout!.swimlane!.configId,
          ),
          dataSourceId: this.viewDataService.dataSourceId,
          data: {
            group: group!,
            layout: {
              type: 'board',
              swimlane: {
                configId: layout!.swimlane!.configId,
                hideEmpty: layout!.swimlane!.hideEmpty,
              },
            },
            filters,
            operator,
          },
          queryParams: { limit: this.SWIMLANE_PAGE_SIZE, page },
        },
      }),
    );
  }

  public moveRecord(item: ViewRecord, moveTo: MoveToParams) {
    this.store.dispatch(
      ViewRecordsActions.moveRecord({
        payload: {
          dataSourceId: this.viewDataService.dataSourceId,
          recordId: item.id,
          targetGroup: { configId: moveTo.groupConfigId, groupId: moveTo.toGroupId },
        },
      }),
    );

    this.actions$.pipe(ofType(ViewRecordsActions.moveRecordSuccess), take(1)).subscribe(() => {
      const params = this.viewDataService.getFormParams();
      const refreshRecords = <const>[
        [moveTo.toGroupId, moveTo.toSwimlaneId],
        [moveTo.fromGroupId, moveTo.fromSwimlaneId],
      ];
      refreshRecords.forEach(([groupId, swimlaneId]) => {
        this.viewDataService.refreshRecordView(
          params,
          { page: 1, limit: this.RECORDS_PAGE_SIZE, totalCount: 0 },
          { configId: moveTo.groupConfigId, value: groupId },
          moveTo.swimlaneConfigId
            ? {
                type: 'board',
                swimlane: {
                  configId: moveTo.swimlaneConfigId,
                  value: swimlaneId!,
                },
              }
            : null,
        );
      });
    });
  }

  private selectRecords(
    swimlaneGrouping: SwimlaneGroupingState,
    grouping: GroupingState,
    hiddenGroups: Record<string, boolean>,
  ) {
    const [swimlaneGroupingId, swimlanesState, swimlanesMeta] = swimlaneGrouping;
    const [groupingId, groupsState, groupsMeta] = grouping;

    const swimlanes = swimlaneGroupingId ? swimlanesState?.swimlanes || [] : [null];
    const groups = (groupsState?.groups || []).filter(
      group => !hiddenGroups[stableStringify(group.id)],
    );
    const data = swimlanes.flatMap((swimlane, swimlaneIndex) =>
      groups.flatMap((group, groupIndex) => {
        const recordsId = this.getRecordId(
          this.viewDataService.dataSourceId,
          groupingId!,
          group.id,
          swimlaneGroupingId,
          swimlane?.id,
        );
        return this.viewDataService
          .getRecordsWithMetaById(recordsId)
          .pipe(map(data => ({ key: `${swimlaneIndex}:${groupIndex}`, data })));
      }),
    );

    /**
     * Stop emitting events when swimlane or grouping changes
     * because records are cleared on swimlane or grouping change
     */
    const recordIdsChanged$ = combineLatest({
      swimlaneState: this.viewDataService.swimlaneState$,
      groupState: this.viewDataService.groupState$,
    }).pipe(
      filter(
        ({ swimlaneState, groupState }) =>
          swimlaneState[0] !== swimlaneGroupingId || groupState[0] !== groupingId,
      ),
    );

    return combineLatest(data).pipe(
      debounceAsap(),
      takeUntil(recordIdsChanged$),
      map((data): SwimlanesData => {
        const dataMap = Object.fromEntries(data.map(({ key, data }) => [key, data]));
        return {
          swimlaneGroupingId,
          swimlanesState,
          swimlanesMeta,
          groupingId,
          groupsState: { ...groupsState!, groups },
          groupsMeta,
          data: swimlanes.map((swimlane, swimlaneIndex) => ({
            swimlane,
            groups: groups.map((group, groupIndex) => ({
              group,
              ...dataMap[`${swimlaneIndex}:${groupIndex}`],
            })),
          })),
        };
      }),
    );
  }

  private getRecordId(
    dataSourceId: string,
    groupingId: string,
    groupId: AnyGroup['id'],
    swimlaneGroupingId: string | null,
    swimlaneId?: Swimlane['id'],
  ) {
    return getRecordsId(
      dataSourceId,
      this.viewDataScope,
      {
        configId: groupingId!,
        value: groupId,
      },
      swimlaneGroupingId
        ? {
            type: 'board',
            swimlane: {
              configId: swimlaneGroupingId,
              value: swimlaneId!,
            },
          }
        : null,
    );
  }

  private watchParamsChanges() {
    this.viewDataService.formParams$.pipe(this.untilDestroyed).subscribe(v => {
      this.viewDataService.clearRecords();
      this.viewDataService.clearGroups();
      this.viewDataService.clearSwimlanes();
    });
  }

  private handleGroupChanges() {
    this.viewDataService.formParams$
      .pipe(distinctUntilChanged(), this.untilDestroyed)
      .subscribe(({ group }) => {
        if (!group?.configId) return;

        this.viewDataService.loadGroups(1, this.GROUP_PAGE_SIZE);
      });
  }

  private watchDataRefresh() {
    inject(dataSourceService$)
      .pipe(
        switchMap(dataSourceService => dataSourceService.refreshData$),
        withLatestFrom(this.swimlanesData$),
        this.untilDestroyed,
      )
      .subscribe(([_, swimlanesData]) => {
        const params = this.viewDataService.getFormParams();
        swimlanesData.data.forEach(swimlaneRecords => {
          swimlaneRecords.groups.forEach(recordData => {
            if (!recordData.data) return;

            const group = { configId: swimlanesData.groupingId!, value: recordData.group!.id };
            const swimlane = swimlanesData.swimlaneGroupingId
              ? <const>{
                  type: 'board',
                  swimlane: {
                    configId: swimlanesData.swimlaneGroupingId,
                    value: swimlaneRecords.swimlane!.id!,
                  },
                }
              : null;
            this.viewDataService.refreshRecordView(params, recordData.data!.meta!, group, swimlane);
          });
        });
      });
  }
}
